import { useState, useEffect, useMemo, useCallback } from "react";
import { Subject } from "rxjs";
import { useAuth } from "../../firebase/AuthProvider";
import { useChatConnection } from "../../contexts/ChatConnectionContext"; // Assuming you have this context for a singleton connection
import {
    Message,
    getMessagesForConversation,
    sendMessage as sendMessageApi,
} from "../../services/chatService/ChatService";
import { toast } from "react-toastify";
import { getUTCTime } from "../../utilities/dateUtils";
import { ValidateOtpResponseDto } from "../../interfaces/ExtermalUserDtos/ExternalUsersDtos";
import { GetHeaders } from "../../services/videoService/VideoService";
import { isNewMessageReceivedSubject } from "../../services/Subjects/ChatSubjects";
import { CHAT_PAGE_SIZE } from "../../Data/SD";

export const conversationRead$ = new Subject<number>();

export const useGlobalChat = (conversationId: number, externalUserDetails?: ValidateOtpResponseDto | null) => {
    const { currentUser } = useAuth();
    const { connection, resetConnection } = useChatConnection(); // Get the global SignalR connection

    // State managed by the hook
    const [messages, setMessages] = useState<Message[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [typingUsers, setTypingUsers] = useState<string[]>([]);

    // Pagination state
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const earliestMessageIdRef = useMemo(() => ({ id: null as number | null }), []);

    // Memoize sound for new messages
    const receiveSound = useMemo(
        () => new Audio("/sounds/message-received.mp3"),
        []
    );

    useEffect(() => {
        if (externalUserDetails) {
            resetConnection(externalUserDetails);
        }
    }, []);

    // Effect for fetching initial messages and setting up listeners
    useEffect(() => {

        if (!connection || !conversationId) return;



        // 1. Fetch initial messages
        const fetchInitialMessages = async () => {
            try {
                setIsLoading(true);
                const token = currentUser ? await currentUser.getIdToken() : "";
                const initialMessages = await getMessagesForConversation(
                    token,
                    conversationId,
                    CHAT_PAGE_SIZE
                );
                const normalizedMessages = initialMessages.map((msg) => ({
                    ...msg,
                    createDate: new Date(getUTCTime(msg.createDate)),
                }));
                // sort to be safe
                normalizedMessages.sort((a,b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime());
                setMessages(normalizedMessages);

                // pagination bookkeeping
                if (normalizedMessages.length > 0) {
                    const minId = Math.min(...normalizedMessages.map(m => m.id));
                    earliestMessageIdRef.id = minId;
                } else {
                    earliestMessageIdRef.id = null;
                }
                setHasMore(normalizedMessages.length === CHAT_PAGE_SIZE);
            } catch (error) {
                console.error("Failed to load messages:", error);
                toast.error("Could not load chat history.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchInitialMessages();

        // 2. Join SignalR group for this conversation
        connection.invoke("JoinConversation", conversationId).then((s) => {
            console.log("Join conversation connected");

        }).catch((err) => {
            console.log("Error join conversation", err)
        });

        // 3. Register SignalR event handlers
        const handleReceiveMessage = (msg: Message) => {
            // Ensure the message belongs to the active conversation
            if (msg.conversationId !== conversationId) {
                return;
            }
            console.log("Received new message from SignalR:", msg);

            // Play sound for messages from other users
            if (msg.author.id !== currentUser?.uid || externalUserDetails?.externalUserDetailId) {
                receiveSound
                    .play()
                    .catch((e) => console.error("Error playing sound:", e));
            }

            setMessages((prevMessages) => {
                const normalizedMsg = {
                    ...msg,
                    createDate: new Date(msg.createDate),
                    isNew: true
                };

                const updatedMessages = [...prevMessages, normalizedMsg];

                updatedMessages.sort(
                    (a, b) =>
                        new Date(a.createDate).getTime() - new Date(b.createDate).getTime()
                );

                // Update earliest id if list was empty previously
                if (earliestMessageIdRef.id === null && updatedMessages.length > 0) {
                    earliestMessageIdRef.id = Math.min(...updatedMessages.map(m => m.id));
                }

                return updatedMessages;
            });

            // for showing the red dot on chat
            isNewMessageReceivedSubject.next(true);
        };

        const handleReceiveReaction = (
            convId: number,
            messageId: number,
            userId: string,
            reactionType: string | null
        ) => {
            if (convId !== conversationId) return;

            setMessages((prevMessages) =>
                prevMessages.map((msg) => {
                    if (msg.id !== messageId) return msg;

                    const existingReactions =
                        msg.reactions?.filter((r) => r.userId !== userId) || [];

                    if (reactionType) {
                        // Add or update the reaction
                        existingReactions.push({ userId, reactionType });
                    }

                    return { ...msg, reactions: existingReactions };
                })
            );
        };
        const handleTypingStatus = (
            convId: number,
            typingUserId: string,
            isTyping: boolean
        ) => {
            // We must check for currentUser here as well, as this callback can fire
            // at any time. This prevents the "cannot read properties of null" error.
            if (
                convId !== conversationId ||
                !currentUser ||
                typingUserId === currentUser.uid
            ) {
                return;
            }

            setTypingUsers((prev) => {
                const otherTypingUsers = prev.filter((id) => id !== typingUserId);
                return isTyping
                    ? [...otherTypingUsers, typingUserId]
                    : otherTypingUsers;
            });
        };

        connection.on("ReceiveMessage", handleReceiveMessage);
        connection.on("ReceiveReaction", handleReceiveReaction);
        connection.on("ReceiveTypingStatus", handleTypingStatus);

        // 4. Cleanup on unmount or when conversation changes
        return () => {
            connection
                .invoke("LeaveConversation", conversationId)
                .catch(console.error);
            connection.off("ReceiveMessage", handleReceiveMessage);
            connection.off("ReceiveReaction", handleReceiveReaction);
            connection.off("ReceiveTypingStatus", handleTypingStatus);
        };
    }, [connection, conversationId, currentUser, receiveSound]);

    // Load more (older) messages - uses earliestMessageIdRef.id as beforeMessageId
    const loadMoreMessages = useCallback(async (pageSize = 50) : Promise<number> => {
        if (!conversationId || loadingMore || !hasMore) return 0;
        if (!currentUser) return 0;
        if (earliestMessageIdRef.id === null) {
            setHasMore(false);
            return 0;
        }

        try {
            setLoadingMore(true);
            const token = currentUser ? await currentUser.getIdToken() : "";
            const olderMessages = await getMessagesForConversation(token, conversationId, pageSize, earliestMessageIdRef.id || undefined);
            if (!olderMessages || olderMessages.length === 0) {
                setHasMore(false);
                return 0;
            }

            const normalized = olderMessages.map((msg) => ({
                ...msg,
                createDate: new Date(getUTCTime(msg.createDate)),
            }));
            // ensure correct ordering
            normalized.sort((a,b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime());

            setMessages((prev) => {
                const newList = [...normalized, ...prev];
                // update earliest id
                earliestMessageIdRef.id = Math.min(...newList.map(m => m.id));
                return newList;
            });

            setHasMore(olderMessages.length === pageSize);
            return normalized.length;
        } catch (err) {
            console.error("Failed to load older messages:", err);
            toast.error("Failed to load older messages.");
            return 0;
        } finally {
            setLoadingMore(false);
        }
    }, [conversationId, currentUser, earliestMessageIdRef, loadingMore, hasMore]);

    const sendTypingStatus = useCallback(
        (isTyping: boolean) => {
            if (connection?.state === "Connected") {
                connection
                    .invoke("SendTypingStatus", conversationId, isTyping)
                    .catch(console.error);
            }
        },
        [connection, conversationId]
    );

    // Function to send a message (handles file uploads internally)
    const sendMessage = async (text: string, file?: File) => {
        // if (!currentUser) return;
        if (!text.trim() && !file) return;

        try {
            const token = currentUser ? await currentUser.getIdToken() : "";
            let attachmentUrl: string | undefined;

            if (file) {
                // ... file upload logic ...
                const formData = new FormData();
                formData.append("file", file);
                console.log("FormData : ", formData);

                const response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/chat/upload-attachment`,
                    {
                        method: "POST",
                        headers: GetHeaders(token, false),
                        body: formData,
                    }
                );
                if (!response.ok) throw new Error("File upload failed.");
                const data = await response.json();
                console.log("Data", data);
                attachmentUrl = data.documentUrl;

                await sendMessageApi(token, conversationId, text, attachmentUrl);
            } else {
                await connection?.invoke("SendMessage", conversationId, text.trim());
            }
        } catch (error) {
            console.error("Failed to send message:", error);
            toast.error("Message could not be sent.");
        }
    };
    // Function to send a reaction
    const sendReaction = async (messageId: number, reactionType: string) => {
        if (!connection) return;
        try {
            await connection.invoke("SendReaction", messageId, reactionType);
        } catch (err) {
            console.error("Error sending reaction:", err);
        }
    };

    const markAsRead = useCallback(() => {
        if (connection?.state === "Connected") {
            connection.invoke("MarkAsRead", conversationId).catch(console.error);
            conversationRead$.next(conversationId);
        }
    }, [connection, conversationId]);

    // Expose state and actions to the component
    return {
        messages,
        isLoading,
        typingUsers,
        sendMessage,
        sendReaction,
        sendTypingStatus,
        markAsRead,
        // pagination exports
        hasMore,
        loadingMore,
        loadMoreMessages,
    };
};
